import { computed, ref } from '@vue/composition-api'
import { SEGMENT_LABELS } from './useRfmAnalysis'

// 顧客一覧の絞り込みUI状態を管理する
// （絞り込みダイアログ・適用中フィルター・チップ表示・個別解除/全解除）
export default function useCustomerListFilters({ applyFilters, resetFilters }) {
  const filterDialog = ref(false)
  const appliedFilters = ref({})

  // 適用中フィルター数
  const activeFilterCount = computed(() => {
    const f = appliedFilters.value
    let count = 0
    if (f.startDate || f.endDate) count += 1
    if (f.rfmSegment && f.rfmSegment.length > 0) count += f.rfmSegment.length
    if (f.minVisitCount) count += 1
    if (f.lastVisitAfter || f.lastVisitBefore) count += 1
    if (f.customerTagIds && f.customerTagIds.length > 0) count += 1
    if (f.castIds && f.castIds.length > 0) count += 1
    if (f.nominationRecipientIds && f.nominationRecipientIds.length > 0) count += 1
    if (f.birthdayMonth) count += 1
    if (f.rootReferrersOnly) count += 1

    return count
  })

  // 適用中フィルターをチップ表示
  const activeFilterChips = computed(() => {
    const f = appliedFilters.value
    const chips = []

    if (f.startDate || f.endDate) {
      const parts = []
      if (f.startDate) parts.push(`${f.startDate}~`)
      if (f.endDate) parts.push(`~${f.endDate}`)
      chips.push({ key: 'period', label: `集計期間: ${parts.join('')}` })
    }
    if (f.rfmSegment && f.rfmSegment.length > 0) {
      f.rfmSegment.forEach(seg => {
        chips.push({ key: `rfm_${seg}`, label: SEGMENT_LABELS[seg] || seg })
      })
    }
    if (f.minVisitCount) {
      chips.push({ key: 'minVisitCount', label: `来店${f.minVisitCount}回以上` })
    }
    if (f.lastVisitAfter || f.lastVisitBefore) {
      const parts = []
      if (f.lastVisitAfter) parts.push(`${f.lastVisitAfter}~`)
      if (f.lastVisitBefore) parts.push(`~${f.lastVisitBefore}`)
      chips.push({ key: 'lastVisit', label: `最終来店: ${parts.join('')}` })
    }
    if (f.customerTagIds && f.customerTagIds.length > 0) {
      chips.push({ key: 'customerTags', label: `タグ: ${f.customerTagIds.length}件` })
    }
    if (f.castIds && f.castIds.length > 0) {
      chips.push({ key: 'casts', label: `関連キャスト: ${f.castIds.length}人` })
    }
    if (f.nominationRecipientIds && f.nominationRecipientIds.length > 0) {
      chips.push({ key: 'nominationRecipients', label: `指名受領: ${f.nominationRecipientIds.length}人` })
    }
    if (f.birthdayMonth) {
      chips.push({ key: 'birthday', label: `誕生日: ${f.birthdayMonth}月` })
    }
    if (f.rootReferrersOnly) {
      chips.push({ key: 'rootReferrers', label: 'ルート紹介者のみ' })
    }

    return chips
  })

  // チップの個別解除
  const removeFilter = key => {
    const f = { ...appliedFilters.value }
    if (key.startsWith('rfm_')) {
      const seg = key.replace('rfm_', '')
      f.rfmSegment = (f.rfmSegment || []).filter(s => s !== seg)
    } else if (key === 'period') {
      f.startDate = null
      f.endDate = null
    } else if (key === 'minVisitCount') {
      f.minVisitCount = null
      f.visitCountStartDate = null
      f.visitCountEndDate = null
    } else if (key === 'lastVisit') {
      f.lastVisitAfter = null
      f.lastVisitBefore = null
    } else if (key === 'customerTags') {
      f.customerTagIds = []
    } else if (key === 'casts') {
      f.castIds = []
    } else if (key === 'nominationRecipients') {
      f.nominationRecipientIds = []
    } else if (key === 'birthday') {
      f.birthdayMonth = null
    } else if (key === 'rootReferrers') {
      f.rootReferrersOnly = false
    }
    appliedFilters.value = f
    applyFilters(f)
  }

  const handleFilterApplyAndClose = filters => {
    appliedFilters.value = { ...filters }
    applyFilters(filters)
    filterDialog.value = false
  }

  const handleFilterResetAndClose = () => {
    appliedFilters.value = {}
    resetFilters()
    filterDialog.value = false
  }

  const handleFilterReset = () => {
    appliedFilters.value = {}
    resetFilters()
  }

  // ルート紹介者絞り込み時のみ紹介売上合計・関係性ツリー列を表示
  const showReferralColumns = computed(() => !!appliedFilters.value.rootReferrersOnly)

  return {
    filterDialog,
    appliedFilters,
    activeFilterCount,
    activeFilterChips,
    showReferralColumns,
    removeFilter,
    handleFilterApplyAndClose,
    handleFilterResetAndClose,
    handleFilterReset,
  }
}
